import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';

import { useWebSocket } from './WebSocketProvider';
import { DataState } from './DataProvider';

const TaskPlanContext = createContext(null);

export const TaskPlanProvider = ({ children }) => {
  const { subscribe, isConnected } = useWebSocket();
  const {
    currentTaskId,
    currentTaskPlan: plan,
    setCurrentTaskPlan: setPlan,
    setCurrentToolCallStatus,
    currentToolCallStatus,
  } = DataState();
  const [planStatus, setPlanStatus] = useState('idle');
  const matchedActionsRef = useRef(new Set());

  useEffect(() => {
    setPlan([]);
    setPlanStatus('idle');
    matchedActionsRef.current = new Set();
    // eslint-disable-next-line
  }, [currentTaskId]);

  useEffect(() => {
    if (
      !plan ||
      plan.length === 0 ||
      !currentToolCallStatus ||
      currentToolCallStatus.length === 0
    )
      return;

    let updated = false;
    const newPlan = plan.map((goal) => {
      const newActions = goal.actions.map((action) => {
        if (action.status === 'done' || action.status === 'cancelled')
          return action;
        if (action.type !== 'tool_call' || !action.tool_name) return action;

        const matchingTool = currentToolCallStatus.find(
          (tc) =>
            tc.function?.name === action.tool_name &&
            (tc.status === 'success' || tc.status === 'failed') &&
            !matchedActionsRef.current.has(`${action.action_id}-${tc.id}`),
        );

        if (matchingTool) {
          matchedActionsRef.current.add(
            `${action.action_id}-${matchingTool.id}`,
          );
          updated = true;
          return {
            ...action,
            status: matchingTool.status === 'success' ? 'done' : 'cancelled',
          };
        }

        const runningTool = currentToolCallStatus.find(
          (tc) =>
            tc.function?.name === action.tool_name && tc.status === 'running',
        );
        if (runningTool && action.status === 'todo') {
          updated = true;
          return { ...action, status: 'in progress' };
        }

        return action;
      });

      const allDone = newActions.every(
        (a) => a.status === 'done' || a.status === 'cancelled',
      );
      const anyInProgress = newActions.some((a) => a.status === 'in progress');
      let goalStatus = goal.status;
      if (allDone && goal.status !== 'done') {
        goalStatus = newActions.every((a) => a.status === 'done')
          ? 'done'
          : 'cancelled';
        updated = true;
      } else if (anyInProgress && goal.status === 'todo') {
        goalStatus = 'in progress';
        updated = true;
      }

      return { ...goal, status: goalStatus, actions: newActions };
    });

    if (updated) {
      setPlan(newPlan);
    }
    // eslint-disable-next-line
  }, [currentToolCallStatus]);

  useEffect(() => {
    if (!isConnected || !currentTaskId) {
      return;
    }

    const handlePlanNotification = (notification) => {
      if (notification.task_id === currentTaskId) {
        if (notification.event_type === 'plan_status') {
          setPlanStatus(notification.status || 'idle');
          return;
        } else {
          setPlan(notification.plan || []);
          matchedActionsRef.current = new Set();
          setPlanStatus('idle');
        }
      }
    };

    const handleToolCallAnalysisStatus = (notification) => {
      if (notification.task_id !== currentTaskId) return;

      const { tool_call_id, status } = notification;

      setCurrentToolCallStatus((prevStatus) =>
        prevStatus.map((toolCall) => {
          if (toolCall.id === tool_call_id) {
            return { ...toolCall, running_phase: status };
          }
          return toolCall;
        }),
      );
    };

    const unsubscribe = subscribe({
      task_plan_notification: handlePlanNotification,
      task_tool_call_analysis_status: handleToolCallAnalysisStatus,
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line
  }, [subscribe, isConnected, currentTaskId]);

  return (
    <TaskPlanContext.Provider value={{ plan, planStatus }}>
      {children}
    </TaskPlanContext.Provider>
  );
};

export const useTaskPlan = () => useContext(TaskPlanContext);
